import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Scissors } from "lucide-react";
import PageMeta from "../components/PageMeta";
import LangToggle, { DraftNote } from "../components/LangToggle";
import useLang from "../lib/lang";
import { REVIEWED_DATES, formatReviewedDate } from "../lib/reviewed";

const OVERLINE = "text-[10px] uppercase tracking-[0.22em] text-ember font-accent font-semibold";

const CARDS = [
  {
    q: { en: "What exactly is the concern — in plain words?", cy: "Beth yn union yw'r pryder — mewn geiriau plaen?" },
    why: {
      en: "A vague worry cannot be answered; a named concern can.",
      cy: "Ni ellir ateb pryder amwys; gellir ateb pryder wedi'i enwi.",
    },
  },
  {
    q: {
      en: "What needs to change, by when — and how will you measure it?",
      cy: "Beth sydd angen newid, erbyn pryd — a sut byddwch chi'n ei fesur?",
    },
    why: { en: "Turns opinion into a testable plan.", cy: "Yn troi barn yn gynllun y gellir ei brofi." },
  },
  {
    q: { en: "Who is doing what, by what date?", cy: "Pwy sy'n gwneud beth, erbyn pa ddyddiad?" },
    why: {
      en: "Every action gets a name and a deadline — including theirs.",
      cy: "Mae enw a dyddiad ar bob cam gweithredu — gan gynnwys eu rhai nhw.",
    },
  },
  {
    q: {
      en: "What support are you offering us — not only what is required of us?",
      cy: "Pa gefnogaeth ydych chi'n ei chynnig i ni — nid dim ond beth sy'n ofynnol gennym?",
    },
    why: {
      en: "Plans are two-way; the law expects help, not just tests.",
      cy: "Mae cynlluniau'n gweithio ddwy ffordd; mae'r gyfraith yn disgwyl cymorth, nid profion yn unig.",
    },
  },
  {
    q: {
      en: "Can you show me where that is written down?",
      cy: "Allwch chi ddangos i mi ble mae hynny wedi'i ysgrifennu?",
    },
    why: {
      en: "Moves the conversation from memory to the record.",
      cy: "Yn symud y sgwrs o'r cof i'r cofnod.",
    },
  },
  {
    q: {
      en: "Please record that I disagree — and my reasons.",
      cy: "Cofnodwch fy mod yn anghytuno, os gwelwch yn dda — a'm rhesymau.",
    },
    why: {
      en: "Calm disagreement, on the record, is how errors get corrected.",
      cy: "Anghytuno'n bwyllog, ar y cofnod, yw sut mae gwallau'n cael eu cywiro.",
    },
  },
  {
    q: {
      en: "What would “good enough” look like to you?",
      cy: "Sut olwg fyddai ar “ddigon da” i chi?",
    },
    why: {
      en: "The standard is good-enough parenting, not perfection — make them say what it means.",
      cy: "Rhianta digon da yw'r safon, nid perffeithrwydd — gwnewch iddyn nhw ddweud beth mae'n ei olygu.",
    },
  },
  {
    q: {
      en: "When will I get the minutes — and how do I correct a mistake in them?",
      cy: "Pryd caf i'r cofnodion — a sut ydw i'n cywiro camgymeriad ynddyn nhw?",
    },
    why: {
      en: "The minutes become the official history; you are entitled to accuracy.",
      cy: "Y cofnodion fydd yr hanes swyddogol; mae gennych hawl i gywirdeb.",
    },
  },
];

const L = {
  en: {
    back: "Back to the meeting prep checklist",
    print: "Print or save as PDF",
    overline: "Meeting prep · Pocket question cards",
    title: "Eight questions to hold in your hand.",
    region: "England & Wales",
    intro:
      "Print on card if you can, cut along the dashed lines, and keep them in a wallet or pocket. In the room, when your mind goes blank — and it will — you only need to read one out. Steady beats eloquent.",
    cardN: (i) => `Card ${i} of 8`,
    why: "Why it works:",
    reviewed: "Last reviewed",
    footerLink: "The full checklist: truth4parents.com/understanding/meeting-prep",
  },
  cy: {
    back: "Yn ôl i'r rhestr baratoi",
    print: "Argraffu neu gadw fel PDF",
    overline: "Paratoi at gyfarfod · Cardiau cwestiwn poced",
    title: "Wyth cwestiwn i'w dal yn eich llaw.",
    region: "Cymru a Lloegr",
    intro:
      "Argraffwch ar gerdyn os gallwch, torrwch ar hyd y llinellau toredig, a chadwch nhw mewn waled neu boced. Yn yr ystafell, pan aiff eich meddwl yn wag — ac fe aiff — does ond angen darllen un allan. Mae pwyllog yn curo huawdl.",
    cardN: (i) => `Cerdyn ${i} o 8`,
    why: "Pam mae'n gweithio:",
    reviewed: "Adolygwyd ddiwethaf",
    footerLink: "Y rhestr lawn: truth4parents.com/understanding/meeting-prep",
  },
};

export default function QuestionCardsPage() {
  const [lang, setLang] = useLang();
  const t = L[lang] || L.en;
  const pick = (o) => o[lang] || o.en;

  return (
    <div data-testid="question-cards-page" className="cheat-sheet question-cards">
      <PageMeta
        title="Pocket question cards — one strong question per card"
        description="Eight wallet-size cut-out cards, each with one strong question to ask in a child protection meeting, and one line on why it works. Print, cut, and keep them in your pocket."
        path="/understanding/meeting-prep/cards"
      />

      {/* Toolbar — hidden on print */}
      <div className="print:hidden bg-linen-alt border-b border-rule">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/understanding/meeting-prep"
            data-testid="question-cards-back-link"
            className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            {t.back}
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <LangToggle lang={lang} setLang={setLang} />
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="question-cards-print-button"
              className="inline-flex items-center gap-2 bg-river text-linen px-5 py-2.5 text-sm font-accent font-semibold tracking-wide hover:bg-river-dark transition-colors"
            >
              <Printer size={14} strokeWidth={1.75} />
              {t.print}
            </button>
          </div>
        </div>
      </div>

      {/* Sheet — sized for A4 */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8 print:p-0 print:max-w-full">
        <article className="bg-white border border-rule print:border-0 p-8 md:p-10 print:p-4 text-[13px] leading-snug text-ink-muted">
          <header className="flex items-end justify-between gap-6 border-b-2 border-ember pb-3 mb-3">
            <div>
              <div className={OVERLINE}>{t.overline}</div>
              <h1 className="font-serif text-2xl md:text-3xl text-ink tracking-tight mt-1 leading-none">
                {t.title}
              </h1>
            </div>
            <div className="text-right text-[10px] text-ink-muted font-accent">
              <div>truth4parents.com</div>
              <div className="mt-0.5">{t.region}</div>
            </div>
          </header>

          <DraftNote lang={lang} />

          <p className="text-[12px] italic text-ink-muted mb-4 mt-3 flex items-start gap-2">
            <Scissors size={13} strokeWidth={1.5} className="mt-0.5 shrink-0 print:hidden" aria-hidden />
            <span>{t.intro}</span>
          </p>

          <div className="cards-grid grid grid-cols-1 sm:grid-cols-2 gap-4 print:gap-2">
            {CARDS.map((card, i) => (
              <div
                key={card.q.en}
                data-testid={`question-card-${i}`}
                className="q-card border-2 border-dashed border-ink/40 bg-white p-5 print:p-3 flex flex-col justify-between min-h-[160px]"
              >
                <div>
                  <div className={OVERLINE}>{t.cardN(i + 1)}</div>
                  <p className="font-serif text-ink text-lg md:text-xl leading-snug tracking-tight mt-2">
                    &ldquo;{pick(card.q)}&rdquo;
                  </p>
                </div>
                <div className="mt-3 pt-2 border-t border-rule/70">
                  <p className="text-[11px] leading-snug text-ink-muted">
                    <strong className="text-ink">{t.why}</strong> {pick(card.why)}
                  </p>
                  <div className="mt-1.5 text-[9px] font-accent text-ink-muted/70">
                    truth4parents.com
                  </div>
                </div>
              </div>
            ))}
          </div>

          <footer className="mt-5 pt-3 border-t border-rule text-[10px] text-ink-muted flex justify-between items-center">
            <div>© You Are Not Alone · truth4parents.com · {t.reviewed} <time dateTime={REVIEWED_DATES["/understanding/meeting-prep/cards"]}>{formatReviewedDate(REVIEWED_DATES["/understanding/meeting-prep/cards"])}</time></div>
            <div>{t.footerLink}</div>
          </footer>
        </article>
      </div>
    </div>
  );
}
